export enum OnboardingFlow {
  Onboarding = 1,
}

export enum OnboardingItemKey {
  // Intro
  GetToKnowPlanny = 1,

  // Setup: Studio
  StudioProfile = 10,
  StudioWizard = 11,
  AddBillingData = 12,

  // Setup: Nomenclatures
  PlanTypes = 20,
  HallTypes = 21,
  ServiceTypes = 22,
  ClassCategories = 23,
  CreatePlan = 24,
  PlanWizard = 25,

  // Team
  EmployeesList = 30,
  EmployeeWizard = 31,
  CreateEvent = 32,
  EventWizard = 33,
  EventWizardHybrid = 34,
  ServiceWizardGuide = 35,
  BlockedTimeGuide = 36,

  // Go Live
  FirstUser = 41,
  UserAdd = 42,
  UserProfile = 43,
  DashboardKpis = 44,
  Statistics = 45,
  StatisticsDetail = 46,
  Support = 47,

  // Page Guides (hidden, component-driven)
  RefillCardGuide = 50,
  RefillCardEmpty = 51,
  InventoryGuide = 52,
  InventoryTransfersGuide = 53,
  CashdeskGuide = 54,
  CashdeskEmpty = 55,
  FamiliesGuide = 56,
  BreakTypesGuide = 57,
  InventoryOverviewGuide = 58,
  GymVisitsGuide = 60,
}

export enum PrepaymentProvider {
  Link = 1,
  Stripe = 2,
}

export enum PlanDurationMetric {
  Days = 10,
  Months = 20,
  Years = 30,
}

export enum PlanConsumerType {
  User = 10,
  Family = 20,
}

export enum UserRole {
  User = 10,
  Employee_Instructor = 12,
  Employee_Instructor_Group_Events = 13,
  Employee_Manager = 15,
  Employee = 20,
  Tenant = 30,
  GlobalAdmin = 40,
}

export enum EventDurationMetric {
  FifteenMin = 15,
  ThirtyMin = 30,
  FortyFiveMin = 45,
  FiftyMin = 50,
  FiftyFiveMin = 55,
  OneHour = 60,
  OneHourFifteenMin = 75,
  OneHourThirtyMin = 90,
  OneHourFortyFiveMin = 105,
  TwoHours = 120,
}

export enum StatisticsType {
  Money = 100,
  Plans = 200,
  Users = 300,
  Event = 400,
  Resources = 500,
  NapAudit = 600,
}

export enum NapVatStatus {
  Registered = 1,
  Article97a = 2,
  NotRegistered = 3,
}

export enum NapXsdValidationStatus {
  Valid = 0,
  Invalid = 1,
  SchemaMissing = 2,
}

export enum ChartType {
  Line = 100,
  Bar = 200,
  Pie = 300,
}

export enum PaymentTypes {
  Card = 100,
  Cash = 200,
  POSCash = 250,
  Multisport = 300,
  Plan = 400,
  Revolut = 450,
  Bank = 500,
  CoolFit = 600,
  MyPos = 700,
  Stripe = 800,

  // Tender-model deposit payment: the class is paid in FULL from the client's
  // deposit ledger. Reservation-only tender — mirrors the backend
  // Planny.Common.Enums.PaymentTypes.Deposit.
  Deposit = 900,

  // Gift-voucher tender: the cashdesk order is paid in FULL from one voucher's
  // remaining balance, and only at the studio that sold it. Cashdesk-only tender —
  // mirrors the backend Planny.Common.Enums.PaymentTypes.GiftVoucher.
  GiftVoucher = 1000,
}

// Lifecycle of a gift voucher. Mirrors Planny.Common.Enums.GiftVoucherStatus.
export enum GiftVoucherStatus {
  // Created but not sold yet: paper, not money. Editable and deletable.
  New = 100,
  // Sold at the cashdesk — carries a balance the studio owes in services.
  Sold = 200,
  // Spent down to zero.
  Redeemed = 300,
  // Validity ran out with balance left; the remainder was written off as breakage.
  Expired = 400,
}

// One movement in a voucher's money ledger.
// Mirrors Planny.Common.Enums.GiftVoucherTransactionType.
export enum GiftVoucherTransactionType {
  Issue = 1,
  Spend = 2,
  Refund = 3,
  Expire = 4,
}

export enum HistoryItemKind {
  Reservation = 100,
  GymVisit = 200,
  Order = 300,
}

export enum RecurringPeriodType {
  Weeks = 10,
  Months = 20,
}

export enum DaysOfWeek {
  Monday = 10,
  Tuesday = 20,
  Wednesday = 30,
  Thursday = 40,
  Friday = 50,
  Saturday = 60,
  Sunday = 70,
}

export enum UserEventStatus {
  Pending = 100,
  Confirmed = 150,
  Arrived = 200,
  OnlyPaid = 300,
  WaitingList = 400,
  Missed = 500,
  PenaltyCancelled = 550,
}

export enum UserPhoneCheckStatus {
  BrandNew = 10,
  FirstLogin = 20,
  Regular = 30,
  PendingVerification = 50,
}

export enum ConsentPolicyType {
  MarketingEmail = 100,
  AutomatedEmails = 200,
  PlannyPrivacyPolicy = 300,
  TenantTermsOfUse = 400,
  PlannyTermsOfUse = 500,
}

export enum EventType {
  Group = 100,
  Individual = 200,
  Service = 300,
  BlockedTime = 400,
}

export enum OperationResultStatus {
  Success = 100,
  Error = 200,
}

export enum StatisticDetailedItemType {
  Reservation = 100,
  UserPlan = 250,
  FamilyPlan = 300,
  Event = 400,
  InstructorVisits = 500,
  InstructorPlans = 600,
  PlanCount = 700,
  ServiceReservation = 750,
  EmailLog = 800,
  SmsLog = 850,
  Order = 900,
  GymVisit = 950,

  // Deposit statistics (Phase 3) — mirrors the backend
  // Planny.Common.Enums.StatisticDetailedItemType additions.
  DepositTransaction = 1000,
  DepositDebtor = 1100,
  DepositLiability = 1150,
}

export enum StudioType {
  Group = 100,
  Service = 200,
  Gym = 300,
  Store = 400,
}

/**
 * In which report the per-employee commission section (and the studio-profit row
 * derived from it) is calculated and shown. Values mirror the backend enum and are
 * deliberately 0-based, so studios saved before the setting existed read as Both.
 */
export enum CommissionsVisibility {
  Both = 0,
  MoneyOnly = 1,
  EventsOnly = 2,
  None = 3,
}

export enum TimeStep {
  FifteenMins = 15,
  ThirtyMins = 30,
  OneHour = 60,
}

export enum DashboardMetricKey {
  // Basic Metrics (Основни метрики)
  CancelledByUser = 100,
  CancelledByAdmin = 200,
  UserReservations = 300,
  AdminReservations = 400,
  UsersOnWaitingList = 500,
  NoShowReservations = 600,
  UnconfirmedReservations = 700,
  ConfirmedRevenue = 800,
  EventsCount = 900,
  NewUserSignups = 1000,
  NoShowRate = 1100,
  ClassCapacityUtilization = 1200,

  // Analytics & KPIs (Анализи и показатели)
  RevenueFromPlans = 1300,
  StaffCommission = 1400,
  NewVsReturningUsersRatio = 1500,
  AverageClassSize = 1600,
  ClassesTaught = 1700,
  PeakHours = 1800,
  MostPopularClasses = 1900,
  TotalActiveUsers = 2000,
  ExpiringPlans = 2100,
  LowAttendanceClasses = 2200,

  // Store / Sales (Магазин)
  StoreTurnover = 2300,
  StoreSalesCount = 2400,
  StoreGrossProfit = 2500,
  StoreAverageOrderValue = 2600,
  StoreTopProducts = 2700,
  StoreLowStock = 2800,
  StoreExpiringProducts = 2900,
}

export enum MetricTrend {
  Up = 100,
  Down = 200,
  Stable = 300,
}

export enum CurrencyType {
  BGN = 10,
  EUR = 20,
}

export enum SlotStatus {
  Available = 0,
  WaitlistAvailable = 1,
  Blocked = 2,
}

export enum MessageStatus {
  Pending = 0,
  Sent = 1,
  Failed = 2,
  Cancelled = 3,
}

// Mirrors the backend Planny.Common.Enums.SubscriptionPaymentMode.
// Decides which subscription plan types may pay for an event:
//   All      — every plan type is accepted (no restriction).
//   Specific — only the plan types linked to the event.
//   None     — no subscription plan may pay for the event.
export enum SubscriptionPaymentMode {
  All = 0,
  Specific = 1,
  None = 2,
}

export enum InvoicePaymentStatus {
  Unpaid = 0,
  Paid = 1,
}

export enum SupportChatStatus {
  Open = 0,
  Closed = 1,
}

export enum SupportChatCategory {
  General = 0,
  Billing = 1,
  Technical = 2,
  Other = 3,
  FeatureRequest = 4,
  Bug = 5,
}

export enum SupportChatPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  Blocker = 3,
}

// Audit timeline actions. Value ranges per entity:
// User/Employee = 10–999 | Event = 1000–1999 | Studio = 2000–2999
export enum AuditTimelineAction {
  UserRegistered = 10,
  UserCardRefilled = 20,
  UserCardEdited = 30,
  UserCardRemoved = 40,
  UserCardRestored = 50,
  UserProfileEdited = 80,
  UserDeleted = 90,
  UserRestored = 100,
  UserPasswordResetLinkGenerated = 110,
  UserReliabilityToggled = 120,
  UserNoteAdded = 130,
  UserNoteEdited = 140,
  UserNoteDeleted = 150,
  UserFamilyAssigned = 160,
  UserFamilyRemoved = 170,
  UserAccountLocked = 180,
  VerificationCodeSent = 190,
  UserEmailConfirmed = 200,
  UserFirstLogin = 210,
  UserLoggedIn = 220,
  EmployeeWorkingHoursUpdated = 230,
  UserPasswordChanged = 240,
  UserOrderCreated = 250,
  UserRfidBandAssigned = 310,
  UserRfidBandRemoved = 320,
  UserReservationsBulkPermanentlyDeleted = 330,
  UserReservationCreated = 340,
  UserReservationCancelled = 350,
  UserGymCheckedIn = 360,
  UserGymCheckedOut = 370,
  UserDepositToppedUp = 380,
  UserDepositWithdrawn = 390,
  UserImpersonatedByAdmin = 400,
  // Recorded on the kept account when another account was merged into it.
  UserAccountsMerged = 410,
  // Recorded on the account that was merged into another one (and then deleted).
  UserMergedIntoAnotherAccount = 420,
}

// Event audit timeline actions. Range: 1000–1999
export enum EventAuditTimelineAction {
  // Event lifecycle
  EventCreated = 1000,
  EventEdited = 1010,
  EventBulkEdited = 1020,
  EventDeleted = 1030,
  EventRestored = 1040,
  EventHardDeleted = 1050,
  EventDuplicated = 1060,
  PortalAppointmentBooked = 1070,
  InstructorBulkAssigned = 1080,

  // User reservations
  ReservationAdded = 1100,
  ReservationDeleted = 1110,
  ReservationPermanentlyDeleted = 1120,
  ReservationRestored = 1130,
  ReservationStatusChanged = 1140,
  ReservationPaymentAdded = 1150,
  ReservationPaymentRemoved = 1160,
  ReservationFormCompletionToggled = 1170,
  ReservationCancelled = 1180,
  WaitingListSpotConfirmed = 1185,
  StripePaymentSessionCreated = 1190,
  StripePaymentCompleted = 1195,

  // Music suggestions
  MusicSuggestionAdded = 1200,
  MusicSuggestionDeleted = 1210,
  MusicSuggestionUpvoted = 1220,
  MusicSuggestionUpvoteRemoved = 1230,

  // Event notes
  EventNoteAdded = 1300,
  EventNoteEdited = 1310,
  EventNoteDeleted = 1320,

  // Reservation notes
  ReservationNoteAdded = 1400,
  ReservationNoteEdited = 1410,
  ReservationNoteDeleted = 1420,

  // Workout plan
  WorkoutPlanEdited = 1500,
  WorkoutPlanDeleted = 1510,
}

// Studio audit timeline actions. Range: 2000–2999
export enum StudioAuditTimelineAction {
  StudioCreated      = 2000,
  StudioEdited       = 2010,
  StudioDeleted      = 2020,
  StudioRestored     = 2030,
  StripeConnected    = 2040,
  StripeDisconnected = 2050,
  // A global admin confirmed opening the studio; shown to its owner in "Моят профил".
  GlobalAdminAccessed = 2060,
  // A global admin saved a change to the studio's settings; shown to its owner in "Моят профил".
  GlobalAdminEditedSettings = 2070,
  // The global admin left the studio for another one; shown to its owner in "Моят профил".
  GlobalAdminLeft = 2080,
  // A global admin opened a user's profile in the studio from the admin panel; shown to its
  // owner in "Моят профил".
  GlobalAdminOpenedProfile = 2090,
  // The owner connected / disconnected the Google account that creates Google Meet links.
  GoogleMeetConnected = 2100,
  GoogleMeetDisconnected = 2110,
}

// The part of the "Редактиране на обект" page a global admin changed (owner's access log).
export enum StudioSettingsSection {
  General = 10,
  WorkingHours = 20,
  Logo = 30,
  QrCodes = 40,
  LinkPage = 50,
}

// Lifecycle status of an inventory transfer (delivery).
// Planned → no stock impact. Completed → lines applied to product stock.
export enum TransferStatus {
  Planned = 10,
  Completed = 20,
}

// Transfer audit timeline actions. Range: 3000–3999
export enum TransferAuditTimelineAction {
  // Transfer lifecycle
  TransferCreated = 3000,
  TransferEdited = 3010,
  TransferDeleted = 3020,
  TransferRestored = 3030,

  // Status changes
  StatusChangedToCompleted = 3100,
  StatusChangedToPlanned = 3110,

  // Line changes
  LineAdded = 3200,
  LineRemoved = 3210,
  LineQuantityChanged = 3220,
  LineExpirationDateChanged = 3230,
  LineProductChanged = 3240,

  // Header field changes
  DeliveryDateChanged = 3300,
  SupplierChanged = 3310,

  // Notes
  NoteAdded = 3400,
  NoteEdited = 3410,
  NoteDeleted = 3420,
}

// Unit of measure for inventory items. Set at creation, never editable.
export enum UnitOfMeasure {
  Piece = 10,
  Gram = 20,
}

// Inventory item audit timeline actions. Range: 4000–4999
export enum InventoryAuditTimelineAction {
  ItemCreated = 4000,
  ItemEdited = 4010,
  ItemDeleted = 4020,
  ItemRestored = 4030,

  PriceChanged = 4040,
  CostPriceChanged = 4050,
  LowStockThresholdChanged = 4060,
  CategoryChanged = 4070,

  ManualQuantityAdjustment = 4080,
  TransferApplied = 4090,
  TransferReversed = 4100,
  ItemSold = 4110,

  NoteAdded = 4120,
  NoteEdited = 4130,
  NoteDeleted = 4140,

  ExpirationDateSet = 4150,
}

export enum EmailType {
  PasswordReset = 10,
  Welcome = 20,
  PortalReservationWaitingList = 30,
  PortalReservationConfirmed = 40,
  PortalServiceReservationConfirmed = 50,
  AdminReservationWaitingList = 60,
  AdminReservationConfirmed = 70,
  AdminServiceReservationConfirmed = 80,
  AdminServiceReservationUpdated = 90,
  WaitingListSpotOpened = 100,
  AppointmentReminder = 110,
  FrontendErrorNotification = 120,
  StudioReservationNotification = 130,
  ReminderReport = 140,
  PrepaymentRequired = 150,
  FormCompletionRequired = 160,
  VerificationCode = 170,
  Invoice = 180,
  InvoiceOverdueWarning = 190,
  InvoiceOverdueReport = 200,
  MassEmail = 210,
  SupportChatNewMessage = 220,
  StudioWelcome = 230,
  ReminderDigest = 290,
  ClientFormRequest = 360,
}

// Mirrors Planny.Common.Enums.ClientFormRequestStatus: what the link of a form sent to a
// client by email shows. Only Pending can be filled in.
export enum ClientFormRequestStatus {
  Pending = 0,
  Completed = 1,
  Expired = 2,
  // The form was deleted, or the client is no longer in the studio.
  Unavailable = 3,
}

// Mirrors the backend Planny.Web.Api.Services.Statistics.CommissionCategory
// (implicit 0-based order). Used to label how a visit's commission was resolved.
export enum CommissionCategory {
  Group = 0,
  Individual = 1,
  Service = 2,
  Multisport = 3,
  CoolFit = 4,
  Plan = 5,
}

// Mirrors the backend Planny.Common.Enums.DepositTransactionType
// (explicit values — must stay in sync with the API contract).
export enum DepositTransactionType {
  TopUp = 1,
  Spend = 2,
  Refund = 3,
  Withdrawal = 4,
}

// Mirrors the backend Planny.Common.Enums.WorkoutAchievementType — personal-record
// badges a trainee earns on a completed set (explicit values, must stay in sync).
export enum WorkoutAchievementType {
  /** Heaviest weight lifted on the exercise (kg of a completed set). */
  MaxWeight = 1,
  /** Biggest single-set volume on the exercise (kg × reps of a completed set). */
  MaxSetVolume = 2,
}

// Mirrors the backend Planny.Common.Enums.BodyMetricType — what a body-metric entry
// measures on the user's "Метрики" page (explicit values, must stay in sync).
export enum BodyMetricType {
  Weight = 1,
  Height = 2,
  BodyFat = 3,
  MuscleMass = 4,
  BodyWater = 5,
  VisceralFat = 6,
  BoneMass = 7,
  RestingHeartRate = 8,
  Waist = 9,
  Chest = 10,
  Hips = 11,
  Biceps = 12,
  Thigh = 13,
  Calf = 14,
  Neck = 15,
  MetabolicAge = 16,
  Vo2Max = 17,
}

// LaunchDarkly flag keys. Keep in sync with FeatureFlagKeys.cs in the API.
// Only flags marked "Available to client-side SDKs" in LaunchDarkly reach the browser.
export enum FeatureFlagKey {
  /** Push notifications: the permission dialog, the profile tab and the subscribe endpoints. */
  EnableAppNotifications = "enable-app-notifications",
  /**
   * Gift vouchers: the "Подаръчни ваучери" sidebar item and the "Подаръчен ваучер" cashdesk
   * tender. SPA only — the API does not read it, so it has no FeatureFlagKeys.cs twin.
   */
  EnableGiftVouchers = "enable-gift-vouchers",
  /**
   * The staff AI assistant in "Поддръжка". The API evaluates it (FeatureFlagKeys.cs) and reports it through
   * AiAssistant/GetStatus, which the SPA reads, so it needs no client-side SDK access in LaunchDarkly.
   */
  EnableAiAssistant = "enable-ai-assistant",
  /**
   * Studio reviews: the "Как беше?" e-mails (read by the automations, AutomationFeatureFlagKeys)
   * and the "Ревюта" sidebar item (read here, so it needs client-side SDK access).
   */
  EnableReviewRequests = "enable-review-requests",
}

// What the reservation-success screen shows about push notifications.
export enum NotificationDialogDecision {
  /** The invitation that leads to the browser permission prompt. */
  Invite = "invite",
  /** The same invitation, leading to the "Add to Home Screen" steps (iPhone/iPad in Safari). */
  IosInvite = "ios-invite",
  None = "none",
}

// Who the notification permission dialog is shown to: clients on the reservation-success
// screens, or staff when they open the platform.
export enum NotificationDialogAudience {
  Client = "client",
  Staff = "staff",
}

// The screen the notification permission dialog is on.
export enum NotificationDialogView {
  Invite = "invite",
  IosSteps = "ios-steps",
  Done = "done",
}

// State of the "Известия" tab in "Моят профил" for the current device.
export enum NotificationsSectionStatus {
  Enabled = "enabled",
  Disabled = "disabled",
  Blocked = "blocked",
  IosInstall = "ios-install",
  Unsupported = "unsupported",
}

// The line under the workout rest timer that explains how to make it ring while the phone is
// locked: turn notifications on, or (iPhone Safari tab) add Planny to the Home Screen first.
export enum RestTimerNotificationHint {
  Enable = "enable",
  IosInstall = "ios-install",
}

// What a push notification was sent for. Keep in sync with PushNotificationType in Enums.cs.
export enum PushNotificationType {
  AppointmentReminder = 10,
  /** The "send test notification" button in the admin panel. */
  Test = 20,
  /** A client booked one of the employee's classes or services. */
  NewReservation = 30,
  /** A spot opened up in a class the client is on the waiting list for. */
  WaitingListSpotOpened = 40,
  /** Planny issued a new invoice to the studio owner. */
  NewInvoice = 50,
}

// Where one device's copy of a push notification got to. Only moves forward:
// Sent → Delivered → Opened; Failed is final. Keep in sync with PushDeliveryStatus in Enums.cs.
export enum PushDeliveryStatus {
  /** The push service accepted it; the device has not confirmed it yet. */
  Sent = 10,
  /** The device's service worker received it and showed the notification. */
  Delivered = 20,
  /** The person clicked the notification. */
  Opened = 30,
  Failed = 40,
}

// Why the push service refused a device's notification. Keep in sync with PushFailureReason in Enums.cs.
export enum PushFailureReason {
  /** The device is no longer subscribed: notifications turned off, or (iPhone) the app was removed from the Home Screen. */
  SubscriptionGone = 10,
  Other = 20,
}

// Outcome of asking the browser for notification permission and saving the subscription.
export enum PushSubscribeResult {
  Subscribed = "subscribed",
  /** The user chose "Block" / "Don't Allow" — the browser remembers it. */
  Denied = "denied",
  /** The system prompt was closed without a choice. */
  Dismissed = "dismissed",
  Unsupported = "unsupported",
  Failed = "failed",
}
